/**
 * ClickHouse SQL dialect + compiler.
 *
 * ClickHouse specifics:
 *   - identifiers quoted with backticks: `col`
 *   - named parameters `{pN:Type}` (ClickHouse does not use positional `?`);
 *     the type is inferred from the JS value (String/Int64/Float64/Bool/…) and
 *     the value stored in `ctx.namedValues`.
 *   - date grouping via `toStartOfHour/Day/Week/Month/Quarter/Year`.
 *   - mutations use `ALTER TABLE … UPDATE/DELETE`; upserts rely on the table
 *     engine (e.g. ReplacingMergeTree) rather than an `ON CONFLICT` clause.
 */
import { SqlCompiler, SqlParamContext, type SqlDialect } from './sql-compiler';
import type { DateGranularity, FieldType } from '../../input/input-types';
import type { FieldRefNode, GroupFieldNode } from '../../plan/query-plan';
import type { AssignmentOp } from '../../plan/mutation-plan';

/** ClickHouse `toStartOf*` function per date granularity. */
const CLICKHOUSE_DATE_FN: Record<DateGranularity, string> = {
    hour: 'toStartOfHour',
    day: 'toStartOfDay',
    week: 'toStartOfWeek',
    month: 'toStartOfMonth',
    quarter: 'toStartOfQuarter',
    year: 'toStartOfYear',
};

/** Infer the ClickHouse parameter type from a JS value. */
function clickHouseType(value: unknown): string {
    if (typeof value === 'number') {
        return Number.isInteger(value) ? 'Int64' : 'Float64';
    }
    if (typeof value === 'boolean') {
        return 'Bool';
    }
    if (value instanceof Date) {
        return 'DateTime';
    }
    return 'String';
}

/** `JSONExtract*` function used to read a nested value, per field type. */
const CLICKHOUSE_JSON_EXTRACT: Record<FieldType, string> = {
    string: 'JSONExtractString',
    number: 'JSONExtractFloat',
    boolean: 'JSONExtractBool',
    date: 'JSONExtractString',
};

export class ClickHouseDialect implements SqlDialect {
    readonly engine = 'clickhouse' as const;

    escapeIdentifier(identifier: string): string {
        // Quote the whole identifier with backticks, escaping embedded backticks.
        return `\`${identifier.split('`').join('``')}\``;
    }

    renderFieldPath(field: FieldRefNode): string {
        // JSONExtract<Type>(`col`, 'key1', 'key2', …) — keys are string literals.
        const fn = CLICKHOUSE_JSON_EXTRACT[field.type];
        const keys = (field.nestedPath ?? [])
            .map((k) => `'${k.split("'").join("''")}'`)
            .join(', ');
        return `${fn}(${this.escapeIdentifier(field.field)}, ${keys})`;
    }

    bindValue(value: unknown, ctx: SqlParamContext): string {
        // ClickHouse uses named params `{name:Type}` passed via `query_params`.
        const name = `p${ctx.next()}`;
        ctx.namedValues[name] = value;
        ctx.values.push(value); // keep positional list in sync for consumers
        return `{${name}:${clickHouseType(value)}}`;
    }

    renderLiteral(value: unknown): string {
        if (value === null || value === undefined) {
            return 'NULL';
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        return `'${String(value).split("'").join("''")}'`;
    }

    renderDateGroup(field: FieldRefNode, group: GroupFieldNode): string {
        const fn = CLICKHOUSE_DATE_FN[group.granularity ?? 'day'];
        return `${fn}(${this.escapeIdentifier(field.field)})`;
    }

    renderInsert(
        table: string,
        columns: string[],
        valuesSql: string,
        _conflict: { keyColumns: string[] } | null,
    ): string {
        // ClickHouse has no ON CONFLICT; upsert semantics rely on the table
        // engine (e.g. ReplacingMergeTree) deduplicating at merge time, so the
        // conflict keys are intentionally ignored here.
        const cols = columns.map((c) => this.escapeIdentifier(c)).join(', ');
        return `INSERT INTO ${table} (${cols}) VALUES ${valuesSql}`;
    }

    renderDelete(table: string, where: string | null): string {
        // ClickHouse mutations use ALTER TABLE ... DELETE WHERE (WHERE required).
        return `ALTER TABLE ${table} DELETE WHERE ${where ?? '1 = 1'}`;
    }

    renderAssignment(
        column: string,
        op: AssignmentOp,
        value: string | null,
    ): string {
        switch (op) {
            case 'increment':
                return `${column} = ${column} + ${value}`;
            case 'append':
                return `${column} = arrayPushBack(${column}, ${value})`;
            case 'removeField':
                return `${column} = NULL`;
            case 'set':
            default:
                return `${column} = ${value}`;
        }
    }

    renderUpdate(
        table: string,
        assignments: string,
        where: string | null,
    ): string {
        return `ALTER TABLE ${table} UPDATE ${assignments} WHERE ${
            where ?? '1 = 1'
        }`;
    }
}

export class ClickHouseCompiler extends SqlCompiler {
    constructor() {
        super(new ClickHouseDialect());
    }
}
